import {
    StyledAutoAwesome,
    StyledField,
} from '@/components/decisionSupport/DecisionSupport.styled';
import { Utils } from '@/types/utils';
import { Typography } from '@mui/material';
import { type ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

type Recommendation = {
    recommendationRank: number;
};

export const useAxleRecommended = <T extends Recommendation>(recommendations?: T[] | null) => {
    return useMemo(
        () =>
            recommendations
                ? recommendations
                      .slice()
                      .sort((a, b) => a.recommendationRank - b.recommendationRank)
                      .at(0) ?? null
                : null,
        [recommendations],
    );
};

// visitAssignment recommendations arrive pre-ranked from the server; rank 0 is
// the top pick. Falls back to the first item if no rank-0 row is present.
export const pickTopRanked = <T extends { rank: number }>(
    recommendations: readonly T[] | undefined | null,
): T | null => recommendations?.find((r) => r.rank === 0) ?? recommendations?.at(0) ?? null;

export const selectionCaption = (hasExplicitPick: boolean): 'Selected' | 'Recommended' =>
    hasExplicitPick ? 'Selected' : 'Recommended';

export const canSelectVisit = (hasRecommendation: boolean, hasExplicitPick: boolean): boolean =>
    hasRecommendation || hasExplicitPick;

export const getRange = <T extends object>(
    list: T[],
    key: Utils.NumberKeys<T>,
): [min: number, max: number] => {
    const items = list.map((item) => item[key] as number);
    return [Math.min(...items), Math.max(...items)];
};

type BuildRecommendedColumnArgs<T> = {
    id: string;
    header: string;
    getLabel: (row: T) => string;
    isRecommended: (row: T) => boolean;
};

export const buildRecommendedColumn = <T,>({
    id,
    header,
    getLabel,
    isRecommended,
}: BuildRecommendedColumnArgs<T>): ColumnDef<T> => ({
    id,
    header,
    cell: ({ row }) => (
        <StyledField>
            <Typography variant='body2'>{getLabel(row.original)}</Typography>
            {isRecommended(row.original) ? <StyledAutoAwesome color='primary' /> : null}
        </StyledField>
    ),
});

export type DecisionSupportSelection = {
    id: string;
    label: string;
};

export type RecommendationRow = {
    id: string;
    label: string;
    // External identifier values for the row's clinician, used for search
    // matching only (not displayed). Absent for non-clinician rows (e.g. dates).
    externalIds?: string[];
    recommendationRank: number;
    factors: Partial<
        Record<
            string,
            {
                sortValue: number | null;
                displayValue: string;
                isSatisfied: boolean | null;
                // Scoring factors only: `false` ⇒ render a neutral "N/A" cell
                // instead of a green→red score. Absent for hard constraints.
                isApplicable?: boolean;
            }
        >
    >;
};

// Search text for a recommendation row: name + id + any external ids. Lets the
// clinician search match on name, clinician id, or external id (display stays
// name-only).
export const recommendationSearchText = (row: RecommendationRow): string =>
    [row.label, row.id, ...(row.externalIds ?? [])].join(' ');

export type NormalizedSlotResult = {
    recommended: RecommendationRow[];
    notRecommended: RecommendationRow[];
    recommendedColumns: ColumnDef<RecommendationRow>[];
    notRecommendedColumns: ColumnDef<RecommendationRow>[];
};

export const EMPTY_RESULT: NormalizedSlotResult = {
    recommended: [],
    notRecommended: [],
    recommendedColumns: [],
    notRecommendedColumns: [],
};
